import Big from 'big.js';

const orderValue = component => component.order == null ? Number.MAX_SAFE_INTEGER : component.order;

export function calculateTaxAmount(taxRule, taxableAmount) {
	const amount = new Big(taxableAmount || 0);
	if (!taxRule || !taxRule.components || !taxRule.components.length || amount.eq(0)) return 0;
	let total = new Big(0);
	const prior = [];
	for (const component of [...taxRule.components].sort((a, b) => orderValue(a) - orderValue(b))) {
		let base = amount;
		if (component.compoundOnOrder !== null && component.compoundOnOrder !== undefined) {
			base = base.plus(prior.filter(row => row.order === component.compoundOnOrder).reduce((sum, row) => sum.plus(row.amount), new Big(0)));
		} else if (component.compound) {
			base = base.plus(total);
		}
		const componentAmount = base.times(component.rate || 0).div(100).round(2, 1);
		total = total.plus(componentAmount);
		prior.push({ order: component.order, amount: componentAmount });
	}
	return Number(total.toString());
}

export function effectiveRate(components) {
	let total = new Big(0);
	const prior = [];
	for (const component of [...(components || [])].sort((a, b) => orderValue(a) - orderValue(b))) {
		let base = new Big(100);
		if (component.compoundOnOrder !== null && component.compoundOnOrder !== undefined) {
			base = base.plus(prior.filter(row => row.order === component.compoundOnOrder).reduce((sum, row) => sum.plus(row.amount), new Big(0)));
		} else if (component.compound) {
			base = base.plus(total);
		}
		const componentAmount = base.times(component.rate || 0).div(100);
		total = total.plus(componentAmount);
		prior.push({ order: component.order, amount: componentAmount });
	}
	return Number(total.round(6, 1).toString());
}

export function allocateTaxAmounts(taxRule, taxableAmounts, fallbackRate = 0) {
	const amounts = taxableAmounts.map(value => new Big(value || 0));
	const taxFor = amount => taxRule && taxRule.components && taxRule.components.length
		? new Big(calculateTaxAmount(taxRule, Number(amount.toString())))
		: amount.times(fallbackRate || 0).div(100).round(2, 1);
	const allocated = amounts.map(taxFor);
	const aggregate = taxFor(amounts.reduce((sum, amount) => sum.plus(amount), new Big(0)));
	const allocatedTotal = allocated.reduce((sum, amount) => sum.plus(amount), new Big(0));
	const lastTaxable = amounts.map(amount => !amount.eq(0)).lastIndexOf(true);
	if (lastTaxable >= 0) allocated[lastTaxable] = allocated[lastTaxable].plus(aggregate.minus(allocatedTotal));
	return allocated.map(amount => Number(amount.toString()));
}

export function describeTaxComponents(components) {
	const sorted = [...components]
		.sort((a, b) => orderValue(a) - orderValue(b));
	return sorted.map(component => {
		const target = sorted.find(candidate => candidate.order === component.compoundOnOrder);
		const compounded = component.compound || component.compoundOnOrder != null;
		return `${component.name || 'Tax'} ${component.rate}%${compounded ? ` compounded${target ? ` on ${target.name || 'Tax'}` : ''}` : ''}`;
	}).join(' + ');
}

export function allocatePayment(paymentAmount, totals) {
	const paymentValue = Number(paymentAmount || 0);
	if (!Number.isFinite(paymentValue)) return { totalPreTax: 0, totalTax: 0 };
	const preTaxValue = Number((totals && totals.totalPreTax) || 0);
	const taxValue = Number((totals && totals.totalTax) || 0);
	if (!Number.isFinite(preTaxValue) || !Number.isFinite(taxValue)) return { totalPreTax: paymentValue, totalTax: 0 };
	const payment = new Big(paymentValue);
	const preTax = new Big(preTaxValue);
	const tax = new Big(taxValue);
	const gross = preTax.plus(tax);
	if (gross.eq(0)) return { totalPreTax: Number(payment.toString()), totalTax: 0 };
	const allocatedPreTax = payment.times(preTax).div(gross).round(2, 1);
	return {
		totalPreTax: Number(allocatedPreTax.toString()),
		totalTax: Number(payment.minus(allocatedPreTax).round(2, 1).toString()),
	};
}
